/**
 * Live chat (G10–G12): the comments an audience leaves at the door, the super
 * chats among them, and the banner the host puts on the stage — which is what
 * every social simulcast carries, so one banner reaches all of them.
 *
 * Money never passes through the platform: the door links to the creator's own
 * PayPal or Cash App, a guest says what they tipped, and a moderator decides
 * whether the banner is earned. The amount is what the guest told us.
 */
export interface LiveComment {
  id: string; name: string; text: string; status: 'pending' | 'shown' | 'hidden';
  amount_cents: number; created_at: string; mine?: boolean; guest_id?: string | null;
}

export const EMOJI = ['❤️', '👏', '🔥', '😂', '🙌', '🤔'];

export type BannerStyle = 'card' | 'bar' | 'bubble';
export const BANNER_STYLES: { id: BannerStyle; label: string }[] = [{ id: 'card', label: 'a card' }, { id: 'bar', label: 'a bar across the screen' }, { id: 'bubble', label: 'a chat bubble' }];

export type BannerPos = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'bottom' | 'top';
export const BANNER_POSITIONS: { id: BannerPos; label: string }[] = [
  { id: 'bottom', label: 'Bottom, full width' }, { id: 'top', label: 'Top, full width' },
  { id: 'bottom-left', label: 'Bottom left' }, { id: 'bottom-right', label: 'Bottom right' },
  { id: 'top-left', label: 'Top left' }, { id: 'top-right', label: 'Top right' },
];

/** What the host console sends the stage over the room's data channel. */
export type StageMessage =
  | { type: 'comment'; id: string; name: string; text: string; amountCents?: number; pos: BannerPos; accent?: string | null; /** G14: where the host dragged it, in percent of the stage; wins over `pos`. */ xy?: [number, number] | null; /** G18: the look. */ style?: BannerStyle }
  | { type: 'poll'; id: string; question: string; options: string[]; tallies: number[]; closed?: boolean; pos: BannerPos; accent?: string | null; xy?: [number, number] | null }
  | { type: 'poll-clear' }
  | { type: 'react'; counts: Record<string, number> }
  | { type: 'now'; title: string }
  | { type: 'clear' };

export function encodeStageMessage(m: StageMessage): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(m));
}

/** A message from anyone in the room is data, not a command: only well-formed ones count. */
export function decodeStageMessage(bytes: Uint8Array): StageMessage | null {
  try {
    const v = JSON.parse(new TextDecoder().decode(bytes)) as Partial<StageMessage> & Record<string, unknown>;
    if (!v || typeof v !== 'object') return null;
    if (v.type === 'clear') return { type: 'clear' };
    if (v.type === 'poll-clear') return { type: 'poll-clear' };
    if (v.type === 'now') return { type: 'now', title: typeof v.title === 'string' ? v.title.slice(0, 60) : '' };
    if (v.type === 'react' && v.counts && typeof v.counts === 'object') {
      const counts: Record<string, number> = {};
      for (const [k, n] of Object.entries(v.counts as Record<string, unknown>)) if (EMOJI.includes(k) && typeof n === 'number' && n > 0) counts[k] = Math.min(200, Math.floor(n));
      return { type: 'react', counts };
    }
    if (v.type === 'poll' && typeof v.id === 'string' && typeof v.question === 'string' && Array.isArray(v.options)) {
      const options = (v.options as unknown[]).filter((o): o is string => typeof o === 'string').map((o) => o.slice(0, 60)).slice(0, 5);
      if (options.length < 2) return null;
      const tallies = options.map((_, i) => { const n = Array.isArray(v.tallies) ? (v.tallies as unknown[])[i] : 0; return typeof n === 'number' && n >= 0 ? Math.floor(n) : 0; });
      const pos = BANNER_POSITIONS.some((p) => p.id === v.pos) ? (v.pos as BannerPos) : 'top-right';
      const accent = typeof v.accent === 'string' && /^#[0-9a-f]{6}$/i.test(v.accent) ? v.accent : null;
      const xy = Array.isArray(v.xy) && v.xy.length === 2 && (v.xy as unknown[]).every((n) => typeof n === 'number' && n >= 0 && n <= 100) ? [v.xy[0], v.xy[1]] as [number, number] : null;
      return { type: 'poll', id: v.id, question: v.question.slice(0, 200), options, tallies, closed: v.closed === true, pos, accent, ...(xy ? { xy } : {}) };
    }
    if (v.type === 'comment' && typeof v.name === 'string' && typeof v.text === 'string' && typeof v.id === 'string') {
      const pos = BANNER_POSITIONS.some((p) => p.id === v.pos) ? (v.pos as BannerPos) : 'bottom';
      const amountCents = typeof v.amountCents === 'number' && v.amountCents > 0 ? Math.min(100000, Math.floor(v.amountCents)) : undefined;
      const accent = typeof v.accent === 'string' && /^#[0-9a-f]{6}$/i.test(v.accent) ? v.accent : null;
      const xy = Array.isArray(v.xy) && v.xy.length === 2 && v.xy.every((n) => typeof n === 'number' && n >= 0 && n <= 100) ? [v.xy[0], v.xy[1]] as [number, number] : null;
      const style = BANNER_STYLES.some((s) => s.id === v.style) ? (v.style as BannerStyle) : 'card';
      return { type: 'comment', id: v.id, name: v.name.slice(0, 60), text: v.text.slice(0, 300), amountCents, pos, accent, ...(xy ? { xy } : {}), style };
    }
    return null;
  } catch { return null; }
}

/** A super chat: a comment whose stated tip meets the room's threshold (0 means every tip counts). */
export function isSuperChat(c: { amount_cents: number }, minCents: number): boolean {
  return c.amount_cents > 0 && c.amount_cents >= minCents;
}

export function money(cents: number): string {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/** The host's queue: what is waiting first, super chats at the top of it, then what is on, then what was hidden. */
export function queueOrder<T extends { status: string; amount_cents: number; created_at: string }>(comments: T[], minCents: number): T[] {
  const rank = (c: T) => (c.status === 'pending' ? (isSuperChat(c, minCents) ? 0 : 1) : c.status === 'shown' ? 2 : 3);
  return [...comments].sort((a, b) => rank(a) - rank(b) || (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0));
}

/** G14: the banner where the host dragged it — a point in percent, kept on the stage; else its named place. */
export function bannerBox(m: { pos: BannerPos; xy?: [number, number] | null }, W = 1920, H = 1080): { left?: number; right?: number; top?: number; bottom?: number; width: number } {
  if (!m.xy) return bannerPlace(m.pos);
  const width = 760;
  const left = Math.max(0, Math.min(W - width, Math.round((m.xy[0] / 100) * W)));
  const top = Math.max(0, Math.min(H - 120, Math.round((m.xy[1] / 100) * H)));
  return { left, top, width };
}

/** Where the banner sits on a 1920×1080 stage. The host moves it from the console. */
export function bannerPlace(pos: BannerPos): { left?: number; right?: number; top?: number; bottom?: number; width: number } {
  const m = 48;
  switch (pos) {
    case 'top': return { left: m, right: m, top: 90, width: 1920 - 2 * m };
    case 'bottom': return { left: m, right: m, bottom: m, width: 1920 - 2 * m };
    case 'top-left': return { left: m, top: 90, width: 760 };
    case 'top-right': return { right: m, top: 90, width: 760 };
    case 'bottom-left': return { left: m, bottom: m, width: 760 };
    case 'bottom-right': return { right: m, bottom: m, width: 760 };
  }
}

/** G15: "Next live" in words — the day and time, and how far off it is; null when none or long past. */
export function nextLiveLine(nextAt: string | null | undefined, now: Date = new Date(), locale?: string): string | null {
  if (!nextAt) return null;
  const at = new Date(nextAt);
  if (Number.isNaN(at.getTime())) return null;
  const diff = at.getTime() - now.getTime();
  const when = at.toLocaleString(locale, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  if (diff < -6 * 3600_000) return null;
  if (diff <= 0) return `Live now — started ${when}`;
  const m = Math.round(diff / 60_000);
  const soon = m < 60 ? `in ${m} min` : m < 48 * 60 ? `in ${Math.floor(m / 60)} h ${m % 60} min` : `in ${Math.round(m / (24 * 60))} days`;
  return `Next live: ${when} · ${soon}`;
}

/** G16: a poll's bars — each answer's share of the votes, as a percentage; all zero when nobody has voted. */
export function pollShares(tallies: number[]): number[] {
  const total = tallies.reduce((a, b) => a + Math.max(0, b), 0);
  return tallies.map((n) => (total ? Math.round((Math.max(0, n) / total) * 100) : 0));
}

/**
 * G17: the emoji to float this tick — the new arrivals since the last counts,
 * capped so a storm reads as a storm and not a wall. Returns the emoji list
 * and the counts to remember.
 */
export function reactionBurst(prev: Record<string, number>, next: Record<string, number>, cap = 12): { float: string[]; seen: Record<string, number> } {
  const float: string[] = [];
  for (const [k, n] of Object.entries(next)) {
    const more = Math.max(0, n - (prev[k] ?? 0));
    for (let i = 0; i < Math.min(more, cap); i++) float.push(k);
  }
  return { float: float.slice(0, cap * 2), seen: next };
}

/** G18: the banner's box and look by style. A bar runs the width of the stage at its row; a bubble is narrower with a round corner. */
export function bannerLook(m: { pos: BannerPos; xy?: [number, number] | null; style?: BannerStyle }, W = 1920, H = 1080): { box: ReturnType<typeof bannerBox>; radius: number; bar: boolean } {
  const style = m.style ?? 'card';
  if (style === 'bar') {
    const b = bannerBox({ pos: m.pos, xy: m.xy }, W, H);
    const top = b.top ?? (b.bottom !== undefined ? undefined : 90);
    return { box: { left: 0, width: W, ...(top !== undefined ? { top } : { bottom: b.bottom ?? 48 }) }, radius: 0, bar: true };
  }
  if (style === 'bubble') {
    const b = bannerBox({ pos: m.pos, xy: m.xy }, W, H);
    return { box: { ...b, width: Math.min(b.width, 640) }, radius: 28, bar: false };
  }
  return { box: bannerBox({ pos: m.pos, xy: m.xy }, W, H), radius: 10, bar: false };
}
